import { useEffect, useState } from "react";
import type { SdkKey, SdkKeyKind } from "@spoonity/flags-contract";
import { createKey, listKeys, revokeKey } from "../api/endpoints";
import { isAdmin, useAuth } from "../auth/AuthContext";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { LoadMore } from "../components/LoadMore";
import { useToast } from "../components/Toast";
import { useWorkspaceEnv } from "../state/WorkspaceEnvContext";

export function SdkKeysPage() {
  const { environment } = useWorkspaceEnv();
  const { identity } = useAuth();
  const { pushError, push } = useToast();
  const admin = isAdmin(identity?.role);

  const [keys, setKeys] = useState<SdkKey[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<SdkKeyKind>("client");
  const [creating, setCreating] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<SdkKey | null>(null);

  async function load() {
    if (!environment || !admin) return;
    setLoading(true);
    try {
      const page = await listKeys(environment.id, { limit: 50 });
      setKeys(page.items);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load SDK keys");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.id, admin]);

  async function loadMore() {
    if (!environment || !nextCursor) return;
    setLoading(true);
    try {
      const page = await listKeys(environment.id, { cursor: nextCursor, limit: 50 });
      setKeys((cur) => [...cur, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load SDK keys");
    } finally {
      setLoading(false);
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!environment) return;
    setCreating(true);
    try {
      const created = await createKey(environment.id, { kind, name });
      setCreatedSecret(created.secret);
      setName("");
      await load();
    } catch (err) {
      pushError(err, "Could not create key");
    } finally {
      setCreating(false);
    }
  }

  async function doRevoke() {
    if (!environment || !revokeTarget) return;
    try {
      await revokeKey(environment.id, revokeTarget.id);
      push({ kind: "success", title: `Key ${revokeTarget.prefix} revoked` });
      await load();
    } catch (err) {
      pushError(err, "Could not revoke key");
    } finally {
      setRevokeTarget(null);
    }
  }

  if (!environment) return null;
  if (!admin) {
    return <EmptyState title="Admin role required" message="SDK keys are visible to admins only." />;
  }

  return (
    <div>
      {createdSecret && (
        <div className="card" style={{ borderColor: "var(--color-success)" }}>
          <strong>Key created — this secret is shown once:</strong>
          <pre className="json-view">{createdSecret}</pre>
          <button className="btn" onClick={() => setCreatedSecret(null)}>
            I've copied it
          </button>
        </div>
      )}

      <form className="card" onSubmit={create}>
        <h3>New key</h3>
        <div className="row wrap">
          <select
            className="select"
            aria-label="Key kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as SdkKeyKind)}
          >
            <option value="client">client</option>
            <option value="server">server</option>
          </select>
          <input
            className="select"
            placeholder="key name"
            aria-label="Key name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <button className="btn primary" type="submit" disabled={creating || !name}>
            Create
          </button>
        </div>
      </form>

      {keys.length === 0 && !loading ? (
        <EmptyState title="No SDK keys yet" />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Prefix</th>
              <th>Kind</th>
              <th>Name</th>
              <th>Created</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td>
                  <code>{k.prefix}…</code>
                </td>
                <td>{k.kind}</td>
                <td>{k.name}</td>
                <td>{new Date(k.createdAt).toLocaleString()}</td>
                <td>{k.revokedAt ? "Revoked" : "Active"}</td>
                <td>
                  {!k.revokedAt && (
                    <button className="btn small danger" onClick={() => setRevokeTarget(k)}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <LoadMore nextCursor={nextCursor} loading={loading} onLoadMore={loadMore} />

      {revokeTarget && (
        <ConfirmDialog
          title="Revoke this key?"
          message={`${revokeTarget.prefix}… will stop working immediately.`}
          confirmLabel="Revoke"
          danger
          onConfirm={doRevoke}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </div>
  );
}
